import { FormEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { agent } from '../api/agent';
import { sessionSelectors, useSessionStore } from '../store/sessionStore';
import { StylePicker } from './StylePicker';

const deriveFallbackName = (brief: string) =>
  brief
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 4)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();

export const ProjectBriefForm = () => {
  const activeProject = useSessionStore(sessionSelectors.activeProject);
  const brief = useSessionStore(sessionSelectors.brief);
  const selectedStyles = useSessionStore(sessionSelectors.selectedStyles);
  const setBrief = useSessionStore((state) => state.setBrief);
  const replaceConcepts = useSessionStore((state) => state.replaceConcepts);
  const renameProject = useSessionStore((state) => state.renameProject);

  const [input, setInput] = useState(brief);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setInput(brief);
  }, [brief]);

  useLayoutEffect(() => {
    const node = textareaRef.current;
    if (node) {
      node.style.height = 'auto';
      node.style.height = `${Math.min(node.scrollHeight, 320)}px`;
    }
  }, [input]);

  const { mutateAsync: generateConcepts, isPending, isSuccess } = useMutation({
    mutationFn: (value: string) => agent.generateConcepts(value, [], selectedStyles)
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) {
      return;
    }

    const result = await generateConcepts(trimmed);
    setBrief(trimmed);
    replaceConcepts(result.concepts);

    if (activeProject) {
      const needsName =
        (!activeProject.name || /^Untitled Project/.test(activeProject.name)) &&
        trimmed.length > 0;

      if (needsName) {
        try {
          const response = await agent.generateProjectName(trimmed, selectedStyles);
          const candidate = response.name.trim();
          if (candidate && candidate !== activeProject.name) {
            renameProject(activeProject.id, candidate);
            return;
          }
        } catch (error) {
          // fall through to local fallback
        }

        const fallback = deriveFallbackName(trimmed);
        if (fallback && fallback !== activeProject.name) {
          renameProject(activeProject.id, fallback);
        }
      }
    }
  };

  const handleChange = (value: string) => {
    setInput(value);
    setBrief(value);
  };

  return (
    <div className="panel-section">
      <header className="section-header">
        <div>
          <h2>Project Brief</h2>
        </div>
      </header>

      <form className="brief-form" onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(event) => handleChange(event.target.value)}
          placeholder="We are building..."
          rows={4}
        />
        <StylePicker />
        <div className="form-actions">
          <button type="submit" className="primary-button" disabled={isPending || !input.trim()}>
            {isPending ? 'Generating…' : isSuccess ? 'Regenerate Concepts' : 'Generate Concepts'}
          </button>
        </div>
      </form>
    </div>
  );
};
